import React, { Component } from "react";
import { connect } from "react-redux";
import { withRouter } from "./utils/withRouter";
import { Routes, Route, Link } from "react-router-dom";
import { get, isEmpty } from "lodash";
import actions from "./actions";
import { queryClient } from "./queries/queryClient";
import { pipedriveUsersQuery } from "./queries/pipedriveUsers";
import { aggregatesQuery } from "./queries/aggregates";
import { labelsQuery } from "./queries/labels";
import { usersQuery } from "./queries/users";
import { appConfigQuery } from "./queries/appConfig";
import { entitiesQuery } from "./queries/entities";
import Loading from "./common/Loading";
import Login from "./main/Login";
import MainLayout from "./main/MainLayout";
import { resetDefaultSettings } from "./utils/settings";
import { withApp } from "./utils/withApp";
import * as Sentry from "@sentry/react";
import ErrorFallback from "./views/common/ErrorFallback";

import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";

import "./App.scss";

// Query keys App reads from the cache and drills into MainLayout — App
// re-renders when any of these settle (see the subscription in componentDidMount).
const DRILLED_QUERY_KEYS = [usersQuery.queryKey[0], appConfigQuery.queryKey[0]];

const mapStateToProps = (state) => ({
  user: get(state.user, "data", {}),
  //   message: get(state.antd, "message", null),
});

const mapDispatchToProps = (dispatch) => ({
  dispatch,
});

class App extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isInitializing: true,
    };

    this.loginUser = this.loginUser.bind(this);
    this.logoutUser = this.logoutUser.bind(this);
  }

  checkAppVersion() {
    const currentVersion = process.env.REACT_APP_VERSION;
    const storedVersion = window.localStorage.getItem("appVersion");

    if (currentVersion && storedVersion && currentVersion !== storedVersion) {
      console.log(
        `App version changed from ${storedVersion} to ${currentVersion}, resetting default localStorage`
      );
      resetDefaultSettings();
    }

    if (currentVersion) {
      window.localStorage.setItem("appVersion", currentVersion);
    }
  }

  async componentDidMount() {
    const { dispatch } = this.props;

    // App reads users/appConfig from the cache (not via hooks), so re-render it
    // when either settles. "updated" filters out observer churn.
    this.unsubscribeQueryCache = queryClient
      .getQueryCache()
      .subscribe((event) => {
        if (get(event, "type") !== "updated") return;

        const rootKey = get(event, "query.queryKey.0");
        if (DRILLED_QUERY_KEYS.includes(rootKey)) {
          this.forceUpdate();
        }
      });

    try {
      const authToken = window.localStorage.getItem("authToken");
      if (authToken) {
        const user = await dispatch(actions.user.getUser());
        if (user) {
          await Promise.all([
            queryClient.ensureQueryData(entitiesQuery).catch(() => {}),
            queryClient.ensureQueryData(appConfigQuery).catch(() => {}),
            queryClient.ensureQueryData(usersQuery).catch(() => {}),
            queryClient.ensureQueryData(labelsQuery).catch(() => {}),
            queryClient.ensureQueryData(aggregatesQuery).catch(() => {}),
            queryClient.ensureQueryData(pipedriveUsersQuery).catch(() => {}),
          ]);
          this.checkAppVersion();
        }
      }
    } catch (error) {
      console.log(error);
      window.localStorage.clear();
    }
    this.setState({ isInitializing: false });
  }

  componentWillUnmount() {
    if (this.unsubscribeQueryCache) this.unsubscribeQueryCache();
  }

  logoutUser() {
    const { dispatch } = this.props;
    dispatch(actions.user.logoutUser());
  }

  async loginUser(values) {
    const { dispatch, message } = this.props;
    try {
      const auth = await dispatch(
        actions.user.authenticateWithGoogle(values.credential)
      );
      if (auth) {
        await dispatch(actions.user.getUser());
        await Promise.all([
          queryClient.ensureQueryData(entitiesQuery).catch(() => {}),
          queryClient.ensureQueryData(appConfigQuery).catch(() => {}),
          queryClient.ensureQueryData(usersQuery).catch(() => {}),
          queryClient.ensureQueryData(labelsQuery).catch(() => {}),
          queryClient.ensureQueryData(aggregatesQuery).catch(() => {}),
          queryClient.ensureQueryData(pipedriveUsersQuery).catch(() => {}),
        ]);
        return true;
      }
      return false;
    } catch (err) {
      console.log(err);
      message.error("Failed to login.");
      return false;
    }
  }

  render() {
    const { user } = this.props;
    const { isInitializing } = this.state;
    const users = queryClient.getQueryData(usersQuery.queryKey) || [];
    const appConfig = queryClient.getQueryData(appConfigQuery.queryKey) || {};

    return (
      <Sentry.ErrorBoundary fallback={ErrorFallback}>
        {isInitializing ? (
          <Loading message={""} />
        ) : (
          <>
            <Routes>
              <Route
                path="maintanence"
                element={
                  <Loading
                    message={"The application is in maintance mode. "}
                    subMessage={
                      <div>
                        Please reach out to glen@taborminerals.com.{" "}
                        <Link to={"/login"}>Try again</Link>
                      </div>
                    }
                  />
                }
              />
              <Route
                path="login"
                element={
                  <Login
                    isAuthenticated={!isEmpty(user)}
                    loginUser={this.loginUser}
                  />
                }
              />
              <Route
                path="/*"
                element={
                  <MainLayout
                    logoutUser={this.logoutUser}
                    isAuthenticated={!isEmpty(user)}
                    users={users}
                    interestFilters={get(appConfig, "interest_filters")}
                    leadStatus={get(appConfig, "lead_status")}
                  />
                }
              />
            </Routes>
          </>
        )}
      </Sentry.ErrorBoundary>
    );
  }
}

export default withApp(
  withRouter(connect(mapStateToProps, mapDispatchToProps)(App))
);
